import { CatalogCategoryType } from '@/contexts/item-context-types';
import { SearchEntryPoint } from '@/screens/partners/types';
import {
  SearchSortOrder,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { useCallback, useEffect, useState } from 'react';
import { useCatalogCategoryListsEnabled } from '@/hooks/dynamic-config/use-catalog-category-lists-enabled';
import { useCatalogFilteringEnabled } from '@/hooks/dynamic-config/use-catalog-filtering-enabled';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { usePrevious } from '@/hooks/use-previous';
import { useSearchVariantPacks } from '@/hooks/use-search-variant-packs';
import { useToasts } from '@/contexts/toast-context';

export interface Props {
  category?: CatalogCategoryType;
  searchEntryPoint?: SearchEntryPoint;
}

export const useFilteredSearchCatalog = ({
  category,
  searchEntryPoint,
}: Props) => {
  const { isGuestMode } = useUserContext();
  const catalogFilteringEnabled = useCatalogFilteringEnabled();
  const catalogCategoryListsEnabled = useCatalogCategoryListsEnabled();
  const { searchSortOrder, setSearchSortOrder } = useUserSettingsContext();
  const { showErrorToast } = useToasts();

  const [userHasSearched, setUserHasSearched] = useState(false);

  const searchCatalog = useSearchVariantPacks({
    category,
    includeGTINFilters: catalogFilteringEnabled && !isGuestMode,
    searchEntryPoint,
    searchSource: 'search_supplier_catalog',
  });

  const { categoryItemGroups, queryRef, searchItemsCallback } = searchCatalog;

  const searchItems = useCallback(
    async (query: string, sortOrder?: SearchSortOrder) => {
      setUserHasSearched(true);
      try {
        await searchItemsCallback(
          query,
          sortOrder || searchSortOrder,
          category,
        );
      } catch (e) {
        showErrorToast();
      }
    },
    [searchItemsCallback, searchSortOrder, showErrorToast, category],
  );

  const changeSortOrder = useCallback(
    async (sortOrder: SearchSortOrder) => {
      setSearchSortOrder(sortOrder);

      // Refetch search result to reflect the sort change
      await searchItems(queryRef.current, sortOrder);
    },
    [setSearchSortOrder, searchItems, queryRef],
  );

  const { categoryItemGroupsInSelectorWithoutAllFilter } =
    useItemGroupCategoryFilters({
      categoryUUIDsToFilterFrom: categoryItemGroups.map(
        (itemGroup) => itemGroup.uuid,
      ),
      filterByCategoryType: !category?.uuid,
      selectedCategoryUUID: category?.uuid,
    });

  const previousSelected = usePrevious(searchCatalog.searchFilters.selected);

  useEffect(() => {
    // OG entry point has default filters that auto-apply on entry — skip search
    // until the user explicitly searches. For other entry points (catalog, etc.),
    // trigger search whenever the user changes filters, including clearing them.
    // previousSelected === undefined skips the first render, where no real
    // filter change has happened yet.
    const skipOrderGuideDefaultSearch =
      !userHasSearched && searchEntryPoint === SearchEntryPoint.ORDER_GUIDE;
    if (skipOrderGuideDefaultSearch || previousSelected === undefined) {
      return;
    }
    searchItems(queryRef.current, searchSortOrder);
    // only need to trigger this effect when the selected filters changes to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCatalog.searchFilters.selected), searchSortOrder]);

  const previousCategoryUUID = usePrevious(category?.uuid);

  useEffect(() => {
    const categoryChanged = category?.uuid !== previousCategoryUUID;
    const isCategoryBrowsingScreen = !searchEntryPoint;

    // using query _or_ filters counts as active search
    const hasActiveSearch =
      Boolean(queryRef.current) ||
      searchCatalog.searchFilters.selected.length > 0;

    const shouldSearchOnCategoryChange =
      categoryChanged &&
      isCategoryBrowsingScreen &&
      (catalogCategoryListsEnabled || hasActiveSearch);

    if (!shouldSearchOnCategoryChange) {
      return;
    }

    searchItems(queryRef.current, searchSortOrder);
  }, [
    category?.uuid,
    previousCategoryUUID,
    searchEntryPoint,
    searchItems,
    searchSortOrder,
    catalogCategoryListsEnabled,
    queryRef,
    searchCatalog.searchFilters.selected.length,
  ]);

  const clearSearch = useCallback(() => {
    if (catalogCategoryListsEnabled && category?.uuid && !searchEntryPoint) {
      searchCatalog.searchFilters.resetFilters();
      searchItems('', searchSortOrder);
    } else {
      searchCatalog.clearSearch();
    }
  }, [
    catalogCategoryListsEnabled,
    category?.uuid,
    searchCatalog,
    searchEntryPoint,
    searchItems,
    searchSortOrder,
  ]);

  return {
    ...searchCatalog,
    catalogCategoryListsEnabled,
    changeSortOrder,
    clearSearch,
    searchItems,
    searchSortOrder,
    subcategories: categoryItemGroupsInSelectorWithoutAllFilter,
    userHasSearched,
  };
};
